import React, { useCallback, useRef, useState, useMemo } from "react";
import { connect } from "react-redux";
import {
    Column,
    DataGrid,
    Selection,
    Export,
    Sorting,
    Paging,
    Toolbar,
    Item,
    Editing,
    FilterRow,
    StateStoring,
    ColumnChooser,
    ColumnChooserSearch,
    Lookup,
    RequiredRule,
} from "devextreme-react/data-grid";
import DataSource from "devextreme/data/data_source";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import { LoadPanel } from 'devextreme-react/load-panel';
import { Button } from 'devextreme-react/button';
import { confirm } from 'devextreme/ui/dialog';
import data from "../../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';
import { GridLayout } from "../../../../common/GridLayout";
import { MESSAGES } from "../../../../common/Messages";
import GridLayoutManager from "../../../../gridLayoutCustomization/GridLayoutManager";
import { ContactMethodOptions } from '../Layout';
import {
    EMPTY_GUID,
    CUSTOMER_ID_TYPE,
    CONTACT_MEMBER_TYPE,
    GRID_KEYS,
    CURRENCY_FORMAT,
    DATETIME_SERIALIZATION_FORMAT,
    DEFAULT_LAYOUT_NAME,
} from '../constants';

const dateTimeEditorOptions = { type: 'datetime' };
// Comment / Follow-up Comment are memo columns (TAbMemo in ARD0009) - edit them in a
// multiline editor rather than a single-line TextBox.
const multilineEditorOptions = { autoResizeEnabled: true };

function DetailsTab(props) {
    const accessToken = props?.oidc?.user?.access_token;
    // A real (saved) header is required before the detail grid can add/focus rows.
    // Without it, focusing a keyless row throws DevExtreme E1042 on the Add page.
    const hasHeader = !!props.headerId && props.headerId !== EMPTY_GUID;
    const hasCustomer = !!props.customerId && props.customerId !== EMPTY_GUID;
    const detailGrid = useRef(null);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [selectedDetailKeys, setSelectedDetailKeys] = useState([]);
    const detailGridKey = GRID_KEYS.FollowUpDetail;
    const [detailLayout, setDetailLayout] = useState(DEFAULT_LAYOUT_NAME);

    const position = { of: '#accordion' };

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    const loadState = useCallback(() => {
        return GridLayout.sendStorageRequest(detailGridKey, "GET", accessToken, null, detailLayout);
    }, [detailLayout, accessToken]);

    const saveState = useCallback((state) => {
        GridLayout.sendStorageRequest(detailGridKey, "POST", accessToken, state, detailLayout);
    }, [detailGridKey, detailLayout, accessToken]);

    // A grid column's <Lookup> needs a Store (or plain array), not a full DataSource instance -
    // passing a DataSource here throws DevExtreme E1016 ("Unexpected type of data source is
    // provided for a lookup column"), so this builds the store directly instead of going through
    // loadDatasource (which wraps it in a DataSource, fine for a grid's own dataSource prop but
    // not for a Lookup column's).
    const makeLookupStore = useCallback((loadUrl, key = 'id', loadParams) => createStore({
        key,
        loadUrl,
        loadParams,
        onBeforeSend: (method, ajaxOptions) => {
            ajaxOptions.headers = {
                Authorization: `Bearer ${accessToken}`,
                access_token: accessToken,
            };
        },
    }), [accessToken]);

    // Contact By is a direct IDMaster reference (matches uARM0009.pas's "left outer join
    // IDMaster ContactBy on ARCOLLECTIONCREDITDETAIL.R_ContactBy = ContactBy.ROWID"), so it's
    // a plain Lookup keyed on that Worker's own RowId.
    const contactByDataSource = useMemo(
        () => makeLookupStore(`${window._env_.REACT_APP_APIS}/${data.ID.GetWorkers}`),
        [makeLookupStore]
    );

    // Contact With is not a direct IDMaster reference: matching uARM0009.pas's qryCustomerContact,
    // ARCOLLECTIONCREDITDETAIL.R_CONTACTWITH points at an IDRELATIONSHIPLINKS row (the customer's
    // relationship to one of their contacts), keyed on that link's own RowId - reuses the same
    // id/Customer/GetIDRelationshiplinks endpoint MasterDataTab/ContactTab already use for a
    // customer's Contact-type relationship links (IDMASTERTYPE=Customer/IDMEMBERTYPE=Contact).
    const contactWithDataSource = useMemo(() => {
        if (!hasCustomer) return [];
        return makeLookupStore(
            `${window._env_.REACT_APP_APIS}/${data.ID.GetIDRelationshiplinks}`,
            'rowid',
            { ROWID: props.customerId, IDTYPE: CUSTOMER_ID_TYPE, IDMEMBERTYPE: CONTACT_MEMBER_TYPE, NAME: 'Contact' }
        );
    }, [makeLookupStore, hasCustomer, props.customerId]);

    const dataSource = useMemo(() => {
        if (!hasHeader) {
            return new DataSource({ store: [], key: "RowId" });
        }

        return new DataSource({
            store: createStore({
                key: "RowId",
                loadUrl: `${window._env_.REACT_APP_APIS}/${data.CollectionCreditDetail.GetCollectionCreditDetails}`,
                insertUrl: `${window._env_.REACT_APP_APIS}/${data.CollectionCreditDetail.AddCollectionCreditDetail}`,
                updateUrl: `${window._env_.REACT_APP_APIS}/${data.CollectionCreditDetail.EditCollectionCreditDetail}`,
                deleteUrl: `${window._env_.REACT_APP_APIS}/${data.CollectionCreditDetail.DeleteCollectionCreditDetail}`,
                loadParams: {
                    collectionHeaderRowId: props.headerId
                },
                onBeforeSend: function (method, ajaxOptions) {
                    ajaxOptions.headers = {
                        Authorization: `Bearer ${accessToken}`,
                        access_token: accessToken,
                    };
                },
                // Add/EditCollectionCreditDetail hand back the complete row (key, RecordNumber
                // and every bound column), so DevExtreme patches the row in place - these only
                // notify, they never refresh/reload the grid (rule 4).
                onInserted: function () {
                    notify(MESSAGES.FOLLOW_UP_DETAIL_INSERT_SUCCESS, "success", 3000);
                },
                onUpdated: function () {
                    notify(MESSAGES.FOLLOW_UP_DETAIL_UPDATE_SUCCESS, "success", 3000);
                },
                onRemoved: function () {
                    notify(MESSAGES.FOLLOW_UP_DETAIL_DELETE_SUCCESS, "success", 3000);
                }
            }),
            key: "RowId",
            paginate: false
        });
    }, [props.headerId, hasHeader, accessToken]);

    const onSelectionChanged = useCallback((e) => {
        setSelectedDetailKeys(e.selectedRowKeys);
    }, []);

    // Matches uARM0009.pas's qryARCollectionCreditDetailAfterInsert: a new follow-up entry
    // defaults ContactDateTime/PromisedDate/NextFollowUpDate to "now" and copies the header's
    // own Reference down as a starting point (still editable per row after that).
    const onInitNewRow = useCallback((e) => {
        const now = new Date();
        e.data.RowId = EMPTY_GUID;
        e.data.RCollectionheader = props.headerId;
        e.data.ContactDateTime = now;
        e.data.PromisedDate = now;
        e.data.NextFollowUpDate = now;
        e.data.Reference = props.headerReference || '';
        // ContactMethod is deliberately left blank - qryARCollectionCreditDetailAfterInsert
        // defaults the three dates and the reference but not the contact method.
    }, [props.headerId, props.headerReference]);

    const onRowInserting = useCallback((e) => {
        e.data.RowId = EMPTY_GUID;
        e.data.RCollectionheader = props.headerId;
    }, [props.headerId]);

    const onRowUpdating = useCallback((e) => {
        e.newData = { ...e.oldData, ...e.newData };
        e.newData.RCollectionheader = props.headerId;
    }, [props.headerId]);

    const onRowInserted = useCallback((e) => {
        if (detailGrid.current?.instance) {
            detailGrid.current.instance().option('focusedRowKey', e.key);
        }
    }, []);

    const onEditingStart = useCallback((e) => {
        const grid = detailGrid.current?.instance?.();
        if (grid && e.key != null) {
            grid.option('focusedRowKey', e.key);
        }
    }, []);

    // Note: previously auto-focused row 0 on first load (focusedRowEnabled + editing
    // mode="cell" together mean a focused row can end up with its first editable cell
    // (Contact By) opened for editing) - removed since nothing should enter edit state
    // before the user actually clicks a row or Add.

    const onDeleteSelected = useCallback(() => {
        const grid = detailGrid.current?.instance?.();
        if (!grid) return;
        const keys = grid.getSelectedRowKeys();
        if (!keys.length) return;
        confirm(MESSAGES.DELETE_SELECTED_RECORD_CONFIRM, MESSAGES.CONFIRM_DELETE).then((yes) => {
            if (!yes) return;
            const store = grid.getDataSource().store();
            Promise.allSettled(keys.map((k) => store.remove(k))).then((results) => {
                results.filter((r) => r.status === "rejected").forEach((r) => {
                    const error = r.reason;
                    const message =
                        (typeof error === "string" ? error : null) ||
                        error?.message ||
                        error?.responseJSON?.message ||
                        MESSAGES.FOLLOW_UP_DETAIL_DELETE_ERROR;
                    notify(message, "error", 5000);
                });
                grid.clearSelection();
                // Not the rule 4 case: these removes go straight to the store, so the grid's
                // own rows are untouched and one refresh (not one per row) is the only way to
                // drop them from the view.
                grid.refresh();
            });
        });
    }, []);

    return (
        <div>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                position={position}
                onHiding={hideLoadPanel}
                visible={loadPanelVisible}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
            />
            <div style={{ height: '40vh' }}>
                <DataGrid
                    dataSource={dataSource}
                    ref={detailGrid}
                    selectedRowKeys={selectedDetailKeys}
                    showBorders={true}
                    rowAlternationEnabled={true}
                    allowColumnReordering={true}
                    allowColumnResizing={true}
                    columnAutoWidth={true}
                    onSelectionChanged={onSelectionChanged}
                    focusedRowEnabled={hasHeader}
                    onInitNewRow={onInitNewRow}
                    onRowInserting={onRowInserting}
                    onRowInserted={onRowInserted}
                    onRowUpdating={onRowUpdating}
                    onEditingStart={onEditingStart}
                    height="100%"
                    noDataText={hasHeader ? MESSAGES.FOLLOW_UP_DETAIL_NO_DATA : MESSAGES.FOLLOW_UP_DETAIL_SAVE_HEADER_FIRST}
                >
                    <StateStoring enabled={true} type="custom" customLoad={loadState} storageKey={detailGridKey} customSave={saveState} />
                    <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                        <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
                    </ColumnChooser>
                    <Selection mode="multiple" showCheckBoxesMode="always" allowSelectAll={false} />
                    <Export enabled={true} allowExportSelectedData={true} />
                    <Editing
                        mode="cell"
                        allowAdding={hasHeader}
                        allowUpdating={hasHeader}
                        allowDeleting={false}
                        newRowPosition="last"
                    />
                    <FilterRow visible={true} />
                    <Sorting mode="none" />
                    <Paging enabled={false} />

                    <Column dataField="RowId" caption="Row ID" visible={false} showInColumnChooser={false} />
                    <Column dataField="RCollectionheader" caption="Header ID" visible={false} showInColumnChooser={false} />
                    <Column dataField="RecordNumber" caption="Record #" dataType="string" allowEditing={false} allowSorting={false} />
                    <Column dataField="RContactby" caption="Contact By" allowSorting={false}>
                        <Lookup dataSource={contactByDataSource} valueExpr="id" displayExpr="name" />
                    </Column>
                    {/* Contact Date and Next Follow-up Date are the two NonBlankFields on
                        uARM0009.dfm's qryARCollectionCreditDetail - required in the legacy
                        screen, so required here too (and re-checked server-side, since a
                        client-only rule is bypassable by calling the API directly). */}
                    <Column
                        dataField="ContactDateTime"
                        caption="Contact Date/Time"
                        dataType="datetime"
                        serializationFormat={DATETIME_SERIALIZATION_FORMAT}
                        editorOptions={dateTimeEditorOptions}
                        allowSorting={false}
                    >
                        <RequiredRule />
                    </Column>
                    {/* R_CONTACTWITH stores an IDRELATIONSHIPLINKS RowId (that customer's link to
                        one of their contacts), not a Contact RowId directly - contactWithDataSource
                        is scoped to this header's own customer, matching qryCustomerContact's
                        R_IDMASTER = :r_Contact filter, so the dropdown only offers that customer's
                        contacts and resolves the saved link back to its contact's name. */}
                    <Column dataField="RContactwith" caption="Contact With" allowSorting={false} allowEditing={hasCustomer}>
                        <Lookup dataSource={contactWithDataSource} valueExpr="rowid" displayExpr="name" />
                    </Column>
                    <Column
                        dataField="ContactMethod"
                        caption="Contact Method"
                        allowSorting={false}
                    >
                        <Lookup dataSource={ContactMethodOptions} valueExpr="id" displayExpr="text" />
                    </Column>
                    <Column dataField="Reference" caption="Reference" dataType="string" allowSorting={false} />
                    <Column dataField="PromisedAmount" caption="Promised Amount" dataType="number" format={CURRENCY_FORMAT} allowSorting={false} />
                    <Column
                        dataField="PromisedDate"
                        caption="Promised Date"
                        dataType="datetime"
                        serializationFormat={DATETIME_SERIALIZATION_FORMAT}
                        editorOptions={dateTimeEditorOptions}
                        allowSorting={false}
                    />
                    <Column
                        dataField="NextFollowUpDate"
                        caption="Next Follow-up Date"
                        dataType="datetime"
                        serializationFormat={DATETIME_SERIALIZATION_FORMAT}
                        editorOptions={dateTimeEditorOptions}
                        allowSorting={false}
                    >
                        <RequiredRule />
                    </Column>
                    <Column
                        dataField="NextFollowUpComment"
                        caption="Follow-up Comment"
                        dataType="string"
                        allowSorting={false}
                        editorType="dxTextArea"
                        editorOptions={multilineEditorOptions}
                    />
                    <Column
                        dataField="Comment"
                        caption="Comment"
                        dataType="string"
                        allowSorting={false}
                        editorType="dxTextArea"
                        editorOptions={multilineEditorOptions}
                    />

                    <Toolbar>
                        <Item name="addRowButton" location="after" />
                        <Item location="after" locateInMenu="never">
                            <Button
                                icon="trash"
                                hint="Delete selected"
                                stylingMode="text"
                                disabled={selectedDetailKeys.length === 0}
                                onClick={onDeleteSelected}
                            />
                        </Item>
                        <Item location="after" locateInMenu="never">
                            <div className="grid-toolbar-sep" />
                        </Item>
                        <Item location="after" locateInMenu="never">
                            <GridLayoutManager gridKey={detailGridKey} dataGrid={detailGrid} setCurrentLayout={setDetailLayout} />
                        </Item>
                        <Item name="saveButton" />
                        <Item name="revertButton" />
                        <Item name="exportButton" />
                        <Item name="columnChooserButton" />
                    </Toolbar>
                </DataGrid>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(DetailsTab);
